// track-row — 53-я controls cell only (no inline wave).
//
// Split-column model: this renders one .tr-row in .rh-controls > .rh-tracks-body.
// The wave for this track is mounted separately by mountTrackWaveRow in
// .rh-waves > .rh-tracks-body. Pairing via --track-index custom property
// (CSS order: var(--track-index) on mobile <768px stacks them).
//
// Web disables all interactive controls (sliders/buttons disabled + opacity 0.5
// via .rh-track-promo class on chips). Click anywhere on .tr-row opens promo
// modal feature=track-mixer.
//
// DOM structure (mirrors desktop controls-column.js 3-col grid):
//   .tr-row.tr-controls-row  [data-track-id] [--track-index]
//   ├── .tr-stripe              (col 1, 4px color swatch)
//   ├── .tr-center-stack        (col 2, 3-row grid: name / chips / VU)
//   │   ├── .tr-name-row > .tr-name
//   │   ├── .tr-chips-row > M + S + R + IN + OUT
//   │   └── .tr-vu-bar > .tr-vu-bar-fill
//   └── .tr-knob-cell           (col 3, 44px — volume knob placeholder)

import type { ShareManifest } from '@bandway/share-types';
import { createKnob } from '@bandway/ui-primitives';
import type { Player } from '../player.js';
import { setState } from '../state.js';

type Track = ShareManifest['tracks'][number];

export interface TrackRowHandle {
  unmount: () => void;
}

interface PlayerWithMaybeConveniences {
  toggleSolo?: (id: string) => void;
  toggleMute?: (id: string) => void;
  setTrackVolume?: (id: string, v: number) => void;
  setVolume?: (id: string, v: number) => void;
  setTrackState?: (id: string, patch: Record<string, unknown>) => void;
  getTrackStates?: () => Map<string, { soloed?: boolean; muted?: boolean }>;
  getTrackVuLevel?: (id: string) => number;
}

export function mountTrackRow(
  host: Element,
  track: Track,
  player: Player,
  trackIndex: number,
): TrackRowHandle {
  const _p = player as unknown as PlayerWithMaybeConveniences;

  // ── CONTROLS ROW ───────────────────────────────────────────────────────
  // Carries both .tr-row (legacy 11-col grid base) and .tr-controls-row
  // (new 3-col grid: 4px 1fr 44px). Later-declared rule wins on
  // grid-template-columns.
  const row = document.createElement('div');
  row.className = 'tr-row tr-controls-row';
  row.dataset.trackId = track.id;
  row.style.setProperty('--track-index', String(trackIndex));

  // Col 1 — color stripe (4px swatch)
  const stripe = document.createElement('div');
  stripe.className = 'tr-stripe';
  stripe.style.background = track.color;
  row.appendChild(stripe);

  // Col 2 — center stack (3-row grid: name / chips / VU)
  const center = document.createElement('div');
  center.className = 'tr-center-stack';

  // Row 1 — name row
  const nameRow = document.createElement('div');
  nameRow.className = 'tr-name-row';
  const name = document.createElement('div');
  name.className = 'tr-name';
  name.textContent = track.name;
  nameRow.appendChild(name);
  center.appendChild(nameRow);

  // Row 2 — chips row: M, S, R, IN, OUT (all disabled for web promo)
  const chipsRow = document.createElement('div');
  chipsRow.className = 'tr-chips-row';

  const muteBtn = document.createElement('button');
  muteBtn.className = 'ms-btn ms-btn-m rh-track-promo';
  muteBtn.type = 'button';
  muteBtn.disabled = true;
  muteBtn.textContent = 'M';
  muteBtn.setAttribute('aria-label', 'Mute');
  chipsRow.appendChild(muteBtn);

  const soloBtn = document.createElement('button');
  soloBtn.className = 'ms-btn ms-btn-s rh-track-promo';
  soloBtn.type = 'button';
  soloBtn.disabled = true;
  soloBtn.textContent = 'S';
  soloBtn.setAttribute('aria-label', 'Solo');
  chipsRow.appendChild(soloBtn);

  // REC chip — promo, opens recorder modal
  const recChip = document.createElement('button');
  recChip.className = 'ms-btn ms-btn-r rh-track-promo';
  recChip.type = 'button';
  recChip.dataset.promo = 'recorder';
  recChip.textContent = 'REC';
  recChip.setAttribute('aria-label', 'Record (Band Way desktop)');
  recChip.addEventListener('click', (e) => {
    e.stopPropagation();
    setState({ promoModal: { open: true, feature: 'recorder' } });
  });
  chipsRow.appendChild(recChip);

  // I/O chips — promo, opens trackIO modal
  const trackAny = track as unknown as {
    inputChannel?: number | string;
    outputChannel?: number | string;
  };
  const inLabel =
    trackAny.inputChannel !== undefined && trackAny.inputChannel !== null
      ? String(trackAny.inputChannel)
      : '1';
  const outLabel =
    trackAny.outputChannel !== undefined && trackAny.outputChannel !== null
      ? String(trackAny.outputChannel)
      : '1-2';

  const inChip = document.createElement('button');
  inChip.className = 'tr-in-sel rh-track-promo';
  inChip.type = 'button';
  inChip.dataset.promo = 'trackIO';
  inChip.textContent = `in ${inLabel}`;
  inChip.setAttribute('aria-label', `Input channel ${inLabel}`);
  inChip.addEventListener('click', (e) => {
    e.stopPropagation();
    setState({ promoModal: { open: true, feature: 'trackIO' } });
  });
  chipsRow.appendChild(inChip);

  const outChip = document.createElement('button');
  outChip.className = 'tr-out-sel rh-track-promo';
  outChip.type = 'button';
  outChip.dataset.promo = 'trackIO';
  outChip.textContent = `out ${outLabel}`;
  outChip.setAttribute('aria-label', `Output channel ${outLabel}`);
  outChip.addEventListener('click', (e) => {
    e.stopPropagation();
    setState({ promoModal: { open: true, feature: 'trackIO' } });
  });
  chipsRow.appendChild(outChip);

  center.appendChild(chipsRow);

  // Row 3 — VU bar (disabled, shows 0 in web promo)
  const vu = document.createElement('div');
  vu.className = 'tr-vu-bar';
  const vuFill = document.createElement('div');
  vuFill.className = 'tr-vu-bar-fill';
  vu.appendChild(vuFill);
  center.appendChild(vu);

  row.appendChild(center);

  // Col 3 — knob cell (volume, disabled in web promo)
  const knobHost = document.createElement('div');
  knobHost.className = 'tr-knob-cell';

  const knob = createKnob({
    initial: track.initialVolume ?? 1,
    min: 0,
    max: 2,
    resetValue: 1,
    onChange: () => {
      // no-op: all controls are promo-disabled in web
    },
  });
  if (knob.el instanceof Element) {
    knobHost.appendChild(knob.el);
  }

  row.appendChild(knobHost);

  // Volume slider (disabled — web promo)
  const volSlider = document.createElement('input');
  volSlider.className = 'tr-volume rh-track-promo';
  volSlider.type = 'range';
  volSlider.min = '0';
  volSlider.max = '1';
  volSlider.step = '0.01';
  volSlider.value = String(track.initialVolume ?? 1);
  volSlider.disabled = true;
  row.appendChild(volSlider);

  // Click anywhere on row → promo modal (track-mixer)
  row.addEventListener('click', () => {
    setState({ promoModal: { open: true, feature: 'track-mixer' } });
  });

  host.appendChild(row);

  return {
    unmount: () => {
      try { host.removeChild(row); } catch { /* best-effort */ }
    },
  };
}
